import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PhoneOutgoing,
  ClipboardList,
  Clock,
  PhoneCall,
  CheckCircle2,
  ShieldCheck,
  AlertTriangle,
  LogOut,
  Timer,
} from "lucide-react";
import { Link } from "react-router-dom";
import SwipeToConfirm from "@/components/SwipeToConfirm";
import { getCurrentPosition } from "@/lib/geo";

const IST_TIME = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
};

const fmtHM = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};

function AttendanceCard() {
  const [status, setStatus] = useState(null); // {present_today, record, ...}
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/mine");
      setStatus(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    const val = pin.trim();
    if (val.length !== 6 || !/^\d{6}$/.test(val)) {
      toast.error("Enter your 6-digit PIN");
      return;
    }
    setSubmitting(true);
    // GPS is mandatory — admins need to know where the clock-in happened.
    // Any error (denied / timeout / no signal) aborts the submit and shows
    // the exact reason so the employee knows what to fix.
    let coords;
    try {
      toast.loading("Getting your location…", { id: "attendance-loc" });
      coords = await getCurrentPosition();
      toast.dismiss("attendance-loc");
    } catch (e) {
      toast.dismiss("attendance-loc");
      toast.error(e?.message || "Location is required to clock in.");
      setSubmitting(false);
      return;
    }
    try {
      const { data } = await api.post("/attendance/checkin", {
        pin: val,
        lat: coords.lat,
        lng: coords.lng,
        accuracy: coords.accuracy,
      });
      if (data.is_late) toast.warning("You're marked present — but you're LATE.");
      else toast.success("You're marked present. Have a great day!");
      setPin("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check in");
    } finally {
      setSubmitting(false);
    }
  };

  const clockOut = async () => {
    setCheckingOut(true);
    let coords;
    try {
      toast.loading("Getting your location…", { id: "attendance-loc" });
      coords = await getCurrentPosition();
      toast.dismiss("attendance-loc");
    } catch (e) {
      toast.dismiss("attendance-loc");
      toast.error(e?.message || "Location is required to clock out.");
      setCheckingOut(false);
      return;
    }
    try {
      await api.post("/attendance/checkout", {
        lat: coords.lat,
        lng: coords.lng,
        accuracy: coords.accuracy,
      });
      toast.success("Clocked out. Great work today!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clock out");
    } finally {
      setCheckingOut(false);
    }
  };

  if (!status) return null;

  const sched = status.schedule || {};

  if (status.present_today && status.record) {
    const r = status.record;
    const late = r.is_late;
    const out = r.checked_out_at;
    const overtime = status.overtime_seconds || 0;
    return (
      <Card
        className={
          "shadow-none " +
          (late
            ? "border-red-300 bg-red-50/60"
            : "border-emerald-200 bg-emerald-50/60")
        }
        data-testid="emp-attendance-present"
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-3">
            <div
              className={
                "w-10 h-10 rounded-full text-white grid place-items-center " +
                (late ? "bg-red-500" : "bg-emerald-500")
              }
            >
              {late ? (
                <AlertTriangle className="w-5 h-5" />
              ) : (
                <CheckCircle2 className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={
                  "text-sm font-medium " +
                  (late ? "text-red-800" : "text-emerald-900")
                }
              >
                {late
                  ? `LATE — checked in at ${IST_TIME(r.checked_in_at)} IST`
                  : `Present since ${IST_TIME(r.checked_in_at)} IST`}
              </div>
              <div
                className={
                  "mono text-[10px] mt-0.5 " +
                  (late ? "text-red-700" : "text-emerald-700")
                }
              >
                Schedule: in by {sched.on_time_cutoff || "10:00"} · out{" "}
                {sched.checkout_time || "18:00"} · end{" "}
                {sched.checkin_end || "20:30"} IST
                {out && ` · Checked out ${IST_TIME(out)}`}
              </div>
            </div>
          </div>

          {out && overtime > 0 && (
            <div
              className="mono text-[11px] text-emerald-800 bg-emerald-100/70 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1.5"
              data-testid="emp-overtime-badge"
            >
              <Timer className="w-3.5 h-3.5" />
              Overtime today: <b>{fmtHM(overtime)}</b>
            </div>
          )}

          {!out && (
            <SwipeToConfirm
              label={checkingOut ? "Clocking out…" : "Swipe to Clock Out"}
              confirmingLabel="Clocking out…"
              onConfirm={clockOut}
              disabled={checkingOut}
              icon={LogOut}
              testId="emp-attendance-checkout"
            />
          )}
        </CardContent>
      </Card>
    );
  }

  if (status.checkin_window_closed) {
    return (
      <Card
        className="border-red-200 shadow-none bg-red-50/60"
        data-testid="emp-attendance-closed"
      >
        <CardContent className="p-3">
          <div className="text-sm font-medium text-red-800">
            Check-in window closed for today
          </div>
          <div className="mono text-[10px] text-red-700 mt-1">
            You&apos;re marked absent. Ask your owner if this is a mistake.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-[#0055FF]/30 shadow-none bg-blue-50/50"
      data-testid="emp-attendance-checkin"
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0055FF]" />
          <div className="mono text-[10px] uppercase tracking-widest text-[#0055FF]">
            Clock in for today
          </div>
        </div>
        <p className="text-xs text-slate-600">
          Enter the 6-digit PIN your owner shared with you.
        </p>
        <div className="flex gap-2">
          <Input
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="••••••"
            className="mono tracking-[0.4em] text-center text-lg font-bold"
            data-testid="emp-attendance-pin-input"
          />
          <Button
            onClick={submit}
            disabled={submitting || pin.length !== 6}
            className="bg-[#0055FF] hover:bg-[#0040CC]"
            data-testid="emp-attendance-submit"
          >
            Clock in
          </Button>
        </div>
        <div className="mono text-[10px] text-slate-500">
          On-time until <b>{sched.on_time_cutoff || "10:00"}</b> IST · window
          closes at <b>{sched.checkin_end || "20:30"}</b> IST.
        </div>
        <div className="mono text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5 leading-relaxed">
          Location Services must be ON. We record where you clock in and out so
          your admin can verify attendance.
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmpHome() {
  const [tasks, setTasks] = useState([]);
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    (async () => {
      const [t, c] = await Promise.all([api.get("/tasks"), api.get("/calls")]);
      setTasks(t.data);
      setCalls(c.data);
    })();
  }, []);

  const pending = tasks.filter((t) => t.status !== "done");
  const todayCalls = calls.length;
  const talkMin = Math.round(
    calls.reduce((a, c) => a + (c.duration_sec || 0), 0) / 60
  );

  return (
    <div className="p-4 space-y-4" data-testid="emp-home">
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#FF5000]">
          Today
        </div>
        <h1 className="font-heading text-2xl font-bold mt-1">Good day 👋</h1>
        <p className="text-sm text-slate-500">
          You have {pending.length} open tasks. Let&apos;s move.
        </p>
      </div>

      <AttendanceCard />

      <div className="grid grid-cols-3 gap-2">
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-3">
            <ClipboardList className="w-4 h-4 text-[#0055FF]" />
            <div className="font-heading text-xl mt-1">{pending.length}</div>
            <div className="mono text-[10px] uppercase text-slate-500">Open</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-3">
            <PhoneCall className="w-4 h-4 text-[#0055FF]" />
            <div className="font-heading text-xl mt-1">{todayCalls}</div>
            <div className="mono text-[10px] uppercase text-slate-500">Calls</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-3">
            <Clock className="w-4 h-4 text-[#FF5000]" />
            <div className="font-heading text-xl mt-1">{talkMin}m</div>
            <div className="mono text-[10px] uppercase text-slate-500">Talk</div>
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-heading font-semibold">Next up</h2>
          <Link
            to="/app/tasks"
            className="mono text-[11px] text-[#0055FF]"
            data-testid="emp-home-alltasks"
          >
            View all →
          </Link>
        </div>
        <div className="space-y-2">
          {pending.slice(0, 3).map((t) => (
            <Card
              key={t.id}
              className="border-slate-200 shadow-none"
              data-testid={`emp-home-task-${t.id}`}
            >
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-sm">{t.title}</div>
                    <div className="mono text-[11px] text-slate-500 mt-0.5">
                      {t.customer_name} · {t.customer_phone}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="w-10 h-10 rounded-full bg-[#0055FF] text-white grid place-items-center"
                    data-testid={`emp-home-call-${t.id}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const { data } = await api.post("/calls/initiate", {
                          task_id: t.id,
                          customer_name: t.customer_name,
                          notes: `From task: ${t.title}`,
                        });
                        toast.success(
                          data.message || "Your phone will ring in a few seconds."
                        );
                      } catch (err) {
                        toast.error(
                          err?.response?.data?.detail || "Could not start call."
                        );
                      }
                    }}
                  >
                    <PhoneOutgoing className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
          {pending.length === 0 && (
            <div className="text-sm text-slate-500 text-center p-6">
              🎉 No open tasks right now.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
